import classNames from "classnames/bind";
import * as React from "react";
import { KeyboardEvent } from "react";
import * as ReactDOM from "react-dom";
import { Keys } from "~/common/enums";
import { Text } from "~/sharedComponents/layout/Text";
import { Marker } from "~/sharedComponents/userflow/utils/marker";
import { commonClientResx } from "../../resxDist/commonClientResx";
import { DefaultFocusPoint } from "../../sharedHOC/adjustContainer/DefaultFocusPoint";
import { FocusTrap } from "../../sharedHOC/adjustContainer/FocusTrap";
import { ButtonCross } from "../buttons";
import { Loading, LoadingTheme } from "../loading";
import { RestoreFocus } from "../restoreFocus";
import { closeModalPopupEvent } from "./events";
import * as styles from "./modalPopup.less";

const classNamesCtx = classNames.bind(styles);

export type ModalPopupProps = {
    stretch?: boolean;
    title?: string;
    information?: string;
    TitleComponent?: JSX.Element;
    InformationComponent?: JSX.Element;
    cross?: boolean;
    theme?: ModalPopupThemes | LegacyModalPopupThemes;
    overlayTheme?: OverlayThemes | OverlayThemes[];
    width?: number | string;
    minWidth?: number | string;
    maxWidth?: number | string;
    autoFocus?: boolean;
    enforceFocus?: boolean;
    close?: () => void;
    /**
     * if set then focus trap tries to return focus back
     * when focus was lost due to active element removal from dom.
     * When active element is removed from dom then browsers silently
     * set focus to body.
     * So if flag is set then component will check (after events that migth lead
     * to element detaching from dom such as mouse click and keyboard events)
     * if active element is body and if yes then restore focus back to first
     * focusable element inside this component
     */
    handleFocusedElementUnmounting?: boolean;
    contentSized?: boolean;
    wrappedTitle?: boolean;
    blockForFullStory?: boolean;
    zIndex?: number;
    onKeyDown?: (event: KeyboardEvent<HTMLDivElement>) => void;
    wrapperClassName?: string;
    isLoading?: boolean;
    containerClass?: string;
} & Marker;

export enum ModalPopupThemes {
    big = "big",
    medium = "medium",
    medium_without_side_borders = "medium_without_side_borders",
    without_borders = "without_borders",
    forecast = "forecast",
    snapshot = "snapshot",
}

/**
 * WARNING!
 * These styles are specific for embedding legacy KO templates into modal.
 * Do not use it for newly added modals.
 */
export enum LegacyModalPopupThemes {
    timelog_custom = "timelog_custom",
    repeat_custom = "repeat_custom",
}

export enum OverlayThemes {
    with_paddings = "with_paddings",
    without_min_sizes = "without_min_sizes",
    quick_search = "quick_search",
}

export const overlayClassName = "react-overlay";

export class ModalPopup extends React.Component<ModalPopupProps> {
    public static defaultProps: Partial<ModalPopupProps> = {
        stretch: false,
        cross: false,
        autoFocus: false,
        enforceFocus: true,
        contentSized: false,
        wrappedTitle: false,
    };

    _popupContainerElement: HTMLDivElement;

    constructor(props: ModalPopupProps) {
        super(props);
        this._popupContainerElement = document.createElement("div");
        this.handleKeyUp = this.handleKeyUp.bind(this);
        this.handleKeyDown = this.handleKeyDown.bind(this);
        document.body.appendChild(this._popupContainerElement);
    }

    handleKeyUp(event: KeyboardEvent<HTMLDivElement>) {
        if (event.key === Keys.Escape) {
            event.stopPropagation();
            // prevents users control closing when user press esc inside invite modal
            event.nativeEvent.stopImmediatePropagation();
            if (!event.defaultPrevented) {
                this.props.close();
            }
        }
    }

    handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
        this.props.onKeyDown?.(event);

        // if modal is opened from editing cell in grid, we must stop "tab",
        // otherwise the handler in Field's editor will finish editing and close the modal
        if (event.key === Keys.Tab) {
            event.stopPropagation();
        }
    }

    handleClosePopup = () => this.props.close();

    componentDidMount() {
        window.addEventListener(closeModalPopupEvent, this.handleClosePopup);
    }

    componentWillUnmount() {
        document.body.removeChild(this._popupContainerElement);
        window.removeEventListener(closeModalPopupEvent, this.handleClosePopup);
    }

    renderContent() {
        const {
            title,
            information,
            cross,
            "data-testid": dataTestId,
            theme,
            overlayTheme,
            width,
            minWidth,
            maxWidth,
            enforceFocus,
            autoFocus,
            wrappedTitle,
            blockForFullStory,
            zIndex,
            TitleComponent,
            InformationComponent,
            isLoading,
        } = this.props;
        const baseClasses = theme ? [styles.modal, styles[theme]] : styles.modal;
        const classes = classNamesCtx(
            baseClasses,
            { [styles.stretch]: this.props.stretch },
            { [styles.viewport_height]: !this.props.contentSized }
        );

        const overlayStyles = { [styles.content_sized]: this.props.contentSized };
        if (overlayTheme) {
            if (Array.isArray(overlayTheme)) {
                overlayTheme.forEach(overTheme => {
                    overlayStyles[styles[overTheme]] = true;
                });
            } else {
                overlayStyles[styles[overlayTheme]] = true;
            }
        }

        const overlayClasses = classNamesCtx(
            styles.overlay,
            overlayStyles,
            overlayClassName,
            this.props.containerClass
        );

        const wrapperStyles = {
            width,
            minWidth,
            maxWidth,
        };

        return (
            <>
                <RestoreFocus />
                <div
                    className={overlayClasses}
                    onKeyUp={this.handleKeyUp}
                    onKeyDown={this.handleKeyDown}
                    data-ep-viewport={true}
                    style={{ zIndex }}
                >
                    {!!isLoading && <Loading fixed={true} theme={LoadingTheme.medium} />}
                    {!isLoading && (
                        <div
                            tabIndex={-1}
                            className={classes}
                            style={wrapperStyles}
                            data-testid={dataTestId}
                            role={"dialog"}
                            aria-modal={true}
                            aria-label={title}
                        >
                            <FocusTrap
                                className={classNamesCtx(styles.wrapper, this.props.wrapperClassName)}
                                autoFocus={autoFocus}
                                enforceFocus={enforceFocus}
                                handleFocusedElementUnmounting={this.props.handleFocusedElementUnmounting}
                            >
                                {(title || information || cross) && (
                                    <div className={styles.title_wrapper}>
                                        <div className={styles.titlebar}>
                                            {TitleComponent}
                                            {title && (
                                                <h2
                                                    className={classNamesCtx({
                                                        [styles.title]: true,
                                                        [styles.wrapped]: wrappedTitle,
                                                        "fs-block": blockForFullStory,
                                                    })}
                                                    title={title}
                                                >
                                                    {title}
                                                </h2>
                                            )}
                                            {cross && (
                                                <div className={styles.close}>
                                                    <ButtonCross
                                                        action={this.props.close}
                                                        focusable={false}
                                                        title={commonClientResx.Close}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                        {InformationComponent}
                                        {information && <Text small={true}>{information}</Text>}
                                    </div>
                                )}
                                {this.props.children}
                                <DefaultFocusPoint container={this._popupContainerElement} />
                            </FocusTrap>
                        </div>
                    )}
                </div>
            </>
        );
    }

    render() {
        return ReactDOM.createPortal(this.renderContent(), this._popupContainerElement);
    }
}
